/**
 * Centre Operations -> Annual Calendar (read-only) and its HQ editor
 * (HQ -> Centre Operations -> Annual Calendar). One page, two nav leaves:
 * the HQ leaf renders it with `editable`, and the API checks again before any
 * change (oh_operations/api/alfred_annual_calendar.py).
 *
 * The academic year runs June -> May. Each entry covers a date range and says
 * which groups it applies to; holidays and optional holidays are days off,
 * training days and events are working days. Types and groups come from the
 * backend, so the page never hard-codes them.
 */
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core'
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab'
import dayjs from 'dayjs'
import React from 'react'

import {
  createCalendarDay,
  deleteCalendarDay,
  fetchAnnualCalendar,
  updateCalendarDay
} from 'api/annualCalendar'
import {
  SearchHeadCell,
  useColumnSearch
} from 'app/components/generic/SearchHeadCell'
import constants from 'app/constants'
import { showToast } from 'app/helpers'

export const KIND_STYLE = {
  holiday: { bg: '#fde2e1', fg: '#b3261e' },
  optional_holiday: { bg: '#fff4e0', fg: '#8a5300' },
  training: { bg: '#efe0d2', fg: '#7a4a12' },
  event: { bg: '#f6d5d0', fg: '#8c2f22' },
  partial_program: { bg: '#fff4cc', fg: '#7a5d00' },
  reopening: { bg: '#e6f4ea', fg: '#1e6b34' },
  celebration: { bg: '#d6f7f7', fg: '#00666b' },
  playdate: { bg: '#ffe2bf', fg: '#8a4b00' }
}

const WEEKDAYS = ['M', 'T', 'W', 'T', 'F', 'S', 'S']

const errorDetail = (e) => e?.response?.data?.detail || constants.ERROR_MESSAGE

const formatRange = (start, end) => {
  const s = dayjs(start)
  if (start === end) return s.format('ddd D MMM YYYY')
  return `${s.format('D MMM')} – ${dayjs(end).format('D MMM YYYY')}`
}

const dayCount = (start, end) => dayjs(end).diff(dayjs(start), 'day') + 1

// date (YYYY-MM-DD) -> entries covering it
export const entriesByDate = (days) => {
  const map = {}
  days.forEach((entry) => {
    for (
      let d = dayjs(entry.start_date);
      !d.isAfter(dayjs(entry.end_date));
      d = d.add(1, 'day')
    ) {
      const key = d.format('YYYY-MM-DD')
      map[key] = [...(map[key] || []), entry]
    }
  })
  return map
}

const rangeText = (d) =>
  formatRange(d.start_date, d.end_date) +
  (d.start_date !== d.end_date
    ? ` (${dayCount(d.start_date, d.end_date)} days)`
    : '')

const EMPTY_FORM = {
  start_date: '',
  end_date: '',
  label: '',
  kind: 'holiday',
  audiences: [],
  notes: ''
}

const DayDialog = ({
  open,
  initial,
  kinds,
  audiences,
  onClose,
  onSave,
  onDelete
}) => {
  const [form, setForm] = React.useState(EMPTY_FORM)
  const [saving, setSaving] = React.useState(false)
  const [confirmDelete, setConfirmDelete] = React.useState(false)
  const [error, setError] = React.useState('')

  React.useEffect(() => {
    if (open) {
      setForm({ ...EMPTY_FORM, ...initial })
      setConfirmDelete(false)
      setError('')
    }
  }, [open, initial])

  const set = (key) => (e) => {
    setForm({ ...form, [key]: e.target.value })
    setError('')
  }

  const toggleAudience = (value) => {
    const has = form.audiences.includes(value)
    setForm({
      ...form,
      audiences: has
        ? form.audiences.filter((a) => a !== value)
        : [...form.audiences, value]
    })
    setError('')
  }

  const run = async (action) => {
    setSaving(true)
    try {
      await action()
    } catch (e) {
      setError(errorDetail(e))
    } finally {
      setSaving(false)
    }
  }

  const submit = () => {
    if (!form.label.trim() || !form.start_date || !form.audiences.length) {
      setError('Fill in the name, the date and who it applies to.')
      return
    }
    run(() => onSave({ ...form, end_date: form.end_date || form.start_date }))
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{initial?.uuid ? 'Edit day' : 'Add day'}</DialogTitle>
      <DialogContent>
        <TextField
          label="Name"
          placeholder="e.g. Diwali"
          value={form.label}
          onChange={set('label')}
          fullWidth
          margin="dense"
          autoFocus
        />
        <Box display="flex" style={{ gap: 12 }}>
          <TextField
            label="From"
            type="date"
            value={form.start_date}
            onChange={set('start_date')}
            margin="dense"
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label="To (blank = one day)"
            type="date"
            value={form.end_date}
            onChange={set('end_date')}
            margin="dense"
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Box>
        <FormControl fullWidth margin="dense">
          <InputLabel>Type</InputLabel>
          <Select value={form.kind} onChange={set('kind')}>
            {kinds.map((k) => (
              <MenuItem key={k.value} value={k.value}>
                {k.label}
                {k.day_off ? ' (day off)' : ' (working day)'}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl component="fieldset" margin="dense">
          <FormLabel component="legend">Applies to</FormLabel>
          <FormGroup>
            {audiences.map((a) => (
              <FormControlLabel
                key={a.value}
                control={
                  <Checkbox
                    size="small"
                    checked={form.audiences.includes(a.value)}
                    onChange={() => toggleAudience(a.value)}
                  />
                }
                label={a.label}
              />
            ))}
          </FormGroup>
        </FormControl>
        <TextField
          label="Notes (optional)"
          value={form.notes}
          onChange={set('notes')}
          fullWidth
          margin="dense"
          multiline
        />
        {error && (
          <Typography color="error" variant="body2" style={{ marginTop: 8 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        {initial?.uuid && (
          <Button
            style={{ color: '#b3261e', marginRight: 'auto' }}
            disabled={saving}
            onClick={() =>
              confirmDelete
                ? run(() => onDelete(initial))
                : setConfirmDelete(true)
            }
          >
            {confirmDelete ? 'Click again to delete' : 'Delete'}
          </Button>
        )}
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={submit}
          disabled={saving}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const MonthGrid = ({ month, byDate, editable, onPick }) => {
  const first = month.startOf('month')
  const blanks = (first.day() + 6) % 7
  const cells = [
    ...Array(blanks).fill(null),
    ...Array.from({ length: month.daysInMonth() }, (_, i) =>
      first.add(i, 'day')
    )
  ]
  const today = dayjs().format('YYYY-MM-DD')

  return (
    <Paper variant="outlined" style={{ padding: 12, height: '100%' }}>
      <Typography variant="subtitle2" gutterBottom>
        {month.format('MMMM YYYY')}
      </Typography>
      <Box
        display="grid"
        gridTemplateColumns="repeat(7, 1fr)"
        style={{ gap: 2 }}
      >
        {WEEKDAYS.map((w, i) => (
          <Typography
            key={`h${i}`}
            variant="caption"
            color="textSecondary"
            align="center"
          >
            {w}
          </Typography>
        ))}
        {cells.map((d, i) => {
          if (!d) return <span key={`b${i}`} />
          const key = d.format('YYYY-MM-DD')
          const entries = byDate[key] || []
          const style = entries.length ? KIND_STYLE[entries[0].kind] : null
          const cell = (
            <Box
              key={key}
              data-testid={`day-${key}`}
              onClick={editable ? () => onPick(key, entries) : undefined}
              style={{
                textAlign: 'center',
                fontSize: 12,
                lineHeight: '24px',
                borderRadius: 4,
                cursor: editable ? 'pointer' : 'default',
                background: style ? style.bg : 'transparent',
                color: style ? style.fg : d.day() === 0 ? '#9e9e9e' : 'inherit',
                fontWeight: style || key === today ? 600 : 400,
                outline: key === today ? '1px solid #616161' : 'none'
              }}
            >
              {d.date()}
            </Box>
          )
          return entries.length ? (
            <Tooltip
              key={key}
              title={entries
                .map((e) => `${e.label} · ${e.kind_label}`)
                .join(' / ')}
            >
              {cell}
            </Tooltip>
          ) : (
            cell
          )
        })}
      </Box>
    </Paper>
  )
}

// List view: fits the page width (no sideways scroll box) so the one-row
// search heading sticks to the top of the window as the page scrolls.
// Alfred's theme sets `white-space: nowrap` on table cells; without `wrap`
// a long "Applies to" ran over Notes and pushed the page sideways.
const wrap = { whiteSpace: 'normal', wordBreak: 'break-word' }

const CalendarList = ({ rows, audienceLabel, canEdit, onPick }) => {
  const columns = React.useMemo(
    () => ({
      dates: rangeText,
      name: (d) => d.label,
      type: (d) => d.kind_label,
      audiences: (d) =>
        d.audiences.map((a) => audienceLabel[a] || a).join(', '),
      notes: (d) => d.notes
    }),
    [audienceLabel]
  )
  const { rows: matching, box, searching } = useColumnSearch(rows, columns)

  return (
    <Paper variant="outlined">
      <Table size="small" style={{ tableLayout: 'fixed', width: '100%' }}>
        <TableHead>
          <TableRow>
            <SearchHeadCell
              label="Dates"
              search={box('dates')}
              style={{ width: '18%' }}
            />
            <SearchHeadCell
              label="Name"
              search={box('name')}
              style={{ width: '18%' }}
            />
            <SearchHeadCell
              label="Type"
              search={box('type')}
              style={{ width: '20%' }}
            />
            <SearchHeadCell
              label="Applies to"
              search={box('audiences')}
              style={{ width: '26%' }}
            />
            <SearchHeadCell label="Notes" search={box('notes')} />
          </TableRow>
        </TableHead>
        <TableBody>
          {matching.length === 0 && (
            <TableRow>
              <TableCell colSpan={5}>
                <Typography variant="body2" color="textSecondary">
                  {searching
                    ? 'No entries match the search.'
                    : 'Nothing on the calendar for this year yet.'}
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {matching.map((d) => (
            <TableRow
              key={d.uuid}
              hover={canEdit}
              style={{ cursor: canEdit ? 'pointer' : 'default' }}
              onClick={canEdit ? () => onPick(d) : undefined}
            >
              <TableCell style={wrap}>{rangeText(d)}</TableCell>
              <TableCell style={wrap}>{d.label}</TableCell>
              <TableCell style={wrap}>
                <Chip
                  size="small"
                  label={d.kind_label}
                  style={{
                    maxWidth: '100%',
                    background: KIND_STYLE[d.kind]?.bg,
                    color: KIND_STYLE[d.kind]?.fg
                  }}
                />
              </TableCell>
              <TableCell style={wrap}>{columns.audiences(d)}</TableCell>
              <TableCell style={wrap}>{d.notes}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  )
}

const AnnualCalendar = ({ editable = false }) => {
  const [data, setData] = React.useState(null)
  const [year, setYear] = React.useState(null)
  const [calendar, setCalendar] = React.useState('bangalore')
  const [audience, setAudience] = React.useState('all')
  const [view, setView] = React.useState('calendar')
  const [dialog, setDialog] = React.useState(null)
  const [error, setError] = React.useState('')

  const load = React.useCallback(async (y, cal) => {
    try {
      const res = await fetchAnnualCalendar(y, cal)
      setData(res)
      setYear(res.year)
      setCalendar(res.calendar || cal || 'bangalore')
      setError('')
    } catch (e) {
      setError(errorDetail(e))
    }
  }, [])

  React.useEffect(() => {
    load()
  }, [load])

  if (error && !data) {
    return (
      <Box p={3}>
        <Typography color="error">{error}</Typography>
      </Box>
    )
  }
  if (!data) {
    return (
      <Box p={6} textAlign="center">
        <CircularProgress />
      </Box>
    )
  }

  // The page decides what to draw; the API decides what may be saved.
  const canEdit = editable && data.can_edit
  const audienceLabel = Object.fromEntries(
    data.audiences.map((a) => [a.value, a.label])
  )
  const shown =
    audience === 'all'
      ? data.days
      : data.days.filter((d) => d.audiences.includes(audience))
  const byDate = entriesByDate(shown)
  const months = Array.from({ length: 12 }, (_, i) =>
    dayjs(`${data.year}-06-01`).add(i, 'month')
  )
  const daysOff = Object.keys(byDate).filter((k) =>
    byDate[k].some((e) => e.day_off)
  ).length

  const pick = (date, entries) =>
    setDialog(
      entries.length
        ? entries[0]
        : { start_date: date, audiences: audience === 'all' ? [] : [audience] }
    )

  const save = async (form) => {
    const body = {
      calendar,
      start_date: form.start_date,
      end_date: form.end_date,
      label: form.label,
      kind: form.kind,
      audiences: form.audiences,
      notes: form.notes
    }
    if (form.uuid) {
      await updateCalendarDay(form.uuid, body)
      showToast('Day updated', constants.SUCCESS)
    } else {
      await createCalendarDay(body)
      showToast('Day added', constants.SUCCESS)
    }
    setDialog(null)
    load(year, calendar)
  }

  const remove = async (entry) => {
    await deleteCalendarDay(entry.uuid)
    showToast('Day removed', constants.SUCCESS)
    setDialog(null)
    load(year, calendar)
  }

  return (
    <Box p={3}>
      <Box
        display="flex"
        alignItems="center"
        flexWrap="wrap"
        style={{ gap: 12 }}
        mb={2}
      >
        {/* Fixed-width boxes: the row must not change size with the value
            picked, or a long one ("Centre leaders (CD / DCD / AM)") pushed
            Applies to onto a second line. */}
        <Typography
          variant="h5"
          style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap' }}
        >
          Annual Calendar
        </Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={view}
          onChange={(e, v) => v && setView(v)}
        >
          <ToggleButton value="calendar">Calendar</ToggleButton>
          <ToggleButton value="list">List</ToggleButton>
        </ToggleButtonGroup>
        {/* TextField select with a pinned label: a bare Select drew "Year"
            on top of the value (the label never moved up). */}
        <TextField
          select
          size="small"
          variant="outlined"
          label="Calendar"
          value={calendar}
          onChange={(e) => load(year, e.target.value)}
          InputLabelProps={{ shrink: true }}
          style={{ width: 190 }}
        >
          {(data.calendars || []).map((c) => (
            <MenuItem key={c.value} value={c.value}>
              {c.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          variant="outlined"
          label="Year"
          value={year ?? data.year}
          onChange={(e) => load(e.target.value, calendar)}
          InputLabelProps={{ shrink: true }}
          style={{ width: 120 }}
        >
          {data.years.map((y) => (
            <MenuItem key={y.value} value={y.value}>
              {y.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          variant="outlined"
          label="Applies to"
          value={audience}
          onChange={(e) => setAudience(e.target.value)}
          InputLabelProps={{ shrink: true }}
          style={{ width: 280 }}
        >
          <MenuItem value="all">Everyone</MenuItem>
          {data.audiences.map((a) => (
            <MenuItem key={a.value} value={a.value}>
              {a.label}
            </MenuItem>
          ))}
        </TextField>
        {canEdit && (
          <Button
            color="primary"
            variant="contained"
            onClick={() =>
              setDialog({ audiences: audience === 'all' ? [] : [audience] })
            }
          >
            Add day
          </Button>
        )}
      </Box>

      <Box
        display="flex"
        alignItems="center"
        flexWrap="wrap"
        style={{ gap: 8 }}
        mb={2}
      >
        {data.kinds.map((k) => (
          <Chip
            key={k.value}
            size="small"
            label={`${k.label}${k.day_off ? '' : ' (working)'}`}
            style={{
              background: KIND_STYLE[k.value]?.bg,
              color: KIND_STYLE[k.value]?.fg
            }}
          />
        ))}
        <Typography
          variant="body2"
          color="textSecondary"
          style={{ marginLeft: 8 }}
        >
          {daysOff} days off in{' '}
          {data.years.find((y) => y.value === year)?.label}
          {audience === 'all' ? '' : ` for ${audienceLabel[audience]}`}
          {canEdit
            ? ` · click a ${view === 'list' ? 'row' : 'day'} to add or edit`
            : ''}
        </Typography>
      </Box>

      {view === 'calendar' && (
        <Grid container spacing={2}>
          {months.map((m) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={m.format('YYYY-MM')}>
              <MonthGrid
                month={m}
                byDate={byDate}
                editable={canEdit}
                onPick={pick}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {view === 'list' && (
        <CalendarList
          rows={shown}
          audienceLabel={audienceLabel}
          canEdit={canEdit}
          onPick={setDialog}
        />
      )}

      {canEdit && (
        <DayDialog
          open={Boolean(dialog)}
          initial={dialog}
          kinds={data.kinds}
          audiences={data.audiences}
          onClose={() => setDialog(null)}
          onSave={save}
          onDelete={remove}
        />
      )}
    </Box>
  )
}

export const AnnualCalendarPage = () => <AnnualCalendar />
export const AnnualCalendarHqPage = () => <AnnualCalendar editable />

export default AnnualCalendar
